import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Edit, Trash2 } from 'lucide-react';
import rbacService from '../services/rbacService';
import { filterAssignableRoles } from '../constants/rbacConstants';
import auditService from '../services/auditService';
import { userService, departmentService } from '../services/firestoreService';
import { roleManagementService } from '../services/mockDatabase';
import RoleCreationModal from './ui/RoleCreationModal';

const RBACManagement = () => {
  const { t } = useTranslation('rbac');
  const [activeTab, setActiveTab] = useState('roles');
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [roleToUpdate, setRoleToUpdate] = useState(null);
  const [updatePermissions, setUpdatePermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Make roles reactive by using state
  const [roles, setRoles] = useState({});
  const [permissions, setPermissions] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(true);

  // Load roles and permissions from database
  useEffect(() => {
    const loadRolesAndPermissions = async () => {
      try {
        setLoadingRoles(true);
        // Wait for RBAC service to be initialized
        await rbacService.initialize();
        const rolesData = filterAssignableRoles(rbacService.getAllRoles());
        const permissionsData = rbacService.getAllPermissions();
        
        // Check if we got valid data
        if (rolesData && Object.keys(rolesData).length > 0) {
          setRoles(rolesData);
          setPermissions(permissionsData);
          console.log('🔍 RBACManagement - Loaded roles from database:', rolesData);
          console.log('🔍 RBACManagement - Loaded permissions from database:', permissionsData);
        } else {
          console.warn('No roles loaded from database, using fallback');
          // Fallback to hardcoded roles if database is empty
          const fallbackRoles = {
            'management': {
              name: 'Management',
              description: 'Full system access and control',
              level: 100,
              permissions: ['system:config', 'users:manage', 'roles:manage', 'audit:read']
            },
            'execution': {
              name: 'Execution',
              description: 'Task execution staff with RFID access',
              level: 30,
              permissions: ['tasks:execute', 'rfid:access']
            },
            'ic_personnel': {
              name: 'IC Personnel',
              description: 'Infection Control specialists',
              level: 80,
              permissions: ['protocols:manage', 'audit:read']
            },
            'administration': {
              name: 'Administration',
              description: 'Department administration',
              level: 70,
              permissions: ['departments:manage', 'users:read']
            },
            'prescriptors': {
              name: 'Prescriptors',
              description: 'Medical staff with patient permissions',
              level: 50,
              permissions: ['patients:read', 'patients:update']
            }
          };
          setRoles(fallbackRoles);
          setPermissions(permissionsData || {});
        }
      } catch (error) {
        console.error('Error loading roles and permissions:', error);
        setError(t('failedToLoadRoles'));
        // Set fallback roles even on error
        const fallbackRoles = {
          'management': {
            name: 'Management',
            description: 'Full system access and control',
            level: 100,
            permissions: ['system:config', 'users:manage', 'roles:manage', 'audit:read']
          }
        };
        setRoles(fallbackRoles);
        setPermissions({});
      } finally {
        setLoadingRoles(false);
      }
    };

    loadRolesAndPermissions();
  }, []);

  // Load real users and their roles
  useEffect(() => {
    const loadUsersAndRoles = async () => {
      let allUsers = null;
      let allUserRoles = null;
      
      try {
        setLoadingUsers(true);
        
        // Get all users from userService
        allUsers = await userService.getAll();
        console.log('🔍 RBACManagement - Loaded users:', allUsers);
        
        // Get all user roles from roleManagementService
        allUserRoles = await roleManagementService.getAll();
        console.log('🔍 RBACManagement - Loaded user roles:', allUserRoles);
        
        // Get all departments
        const allDepartments = await departmentService.getAll();
        console.log('🔍 RBACManagement - Loaded departments:', allDepartments);
        setDepartments(allDepartments || []);
        
        // Ensure allUsers is an array
        if (!Array.isArray(allUsers)) {
          console.error('allUsers is not an array:', allUsers);
          setUsers([]);
          return;
        }
        
        // Ensure allUserRoles is an array
        if (!Array.isArray(allUserRoles)) {
          console.error('allUserRoles is not an array:', allUserRoles);
          setUsers([]);
          return;
        }
        
        // Combine users with their role information
        const usersWithRoles = allUsers.map(user => {
          // Find the most recent active role for this user
          const userActiveRoles = allUserRoles.filter(ur => 
            ur.user_id === user.id && !ur.end_date
          ).sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
          
          const currentRole = userActiveRoles[0];
          
          // Try to get role info from user_roles collection first, then fall back to user.role_id
          const roleId = currentRole?.role_id || user.role_id;
          const roleInfo = roleId ? roles[roleId] : null;
          
          console.log('🔍 User role lookup:', {
            userId: user.id,
            userName: user.full_name,
            userRoleId: user.role_id,
            currentRoleFromHistory: currentRole?.role_id,
            finalRoleId: roleId,
            roleInfo: roleInfo?.name || 'NOT FOUND'
          });
          
          // Find department information
          const userDepartment = allDepartments.find(dept => 
            dept.id === user.primary_department_id || 
            (user.department_id && dept.id === user.department_id)
          );
          
          return {
            id: user.id,
            fullName: user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || t('unknownUser'),
            email: user.email || t('noEmail'),
            roleId: roleId || 'unknown',
            roleName: roleInfo?.name || t('noRoleAssigned'),
            departmentId: user.primary_department_id || user.department_id,
            departmentName: userDepartment ? userDepartment.name : t('noDepartment'),
            lastLogin: user.last_login ? new Date(user.last_login).toLocaleString() : t('lastLogin'),
            status: user.status === 'active' ? t('statusActive') : t('statusInactive'),
            badgeId: user.badge_id,
            employeeId: user.employee_id,
            phone: user.phone_primary
          };
        });
        
        setUsers(usersWithRoles);
        setUserRoles(allUserRoles);
        
      } catch (error) {
        console.error('❌ Error loading users:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          allUsers: allUsers,
          allUserRoles: allUserRoles
        });
        setError(t('failedToLoadUsers'));
      } finally {
        setLoadingUsers(false);
      }
    };

    loadUsersAndRoles();
  }, [refreshKey, roles, t]);

  const handleRoleSelect = (roleKey) => {
    console.log('🔍 handleRoleSelect called with:', roleKey);
    console.log('🔍 Available roles:', Object.keys(roles));
    console.log('🔍 Role data:', roles[roleKey]);
    console.log('🔍 Current selected role:', selectedRole);
    
    if (!roles[roleKey]) {
      console.error('Role not found:', roleKey);
      setError(`Role not found: ${roleKey}`);
      return;
    }
    
    // Toggle: if same role is selected, hide it; otherwise show it
    if (selectedRole === roleKey) {
      console.log('🔍 Hiding role details');
      setSelectedRole(null);
    } else {
      console.log('🔍 Showing role details for:', roleKey);
      setSelectedRole(roleKey);
    }
    
    setSelectedPermission(null);
    setError(null);
  };

  const handlePermissionSelect = (permissionKey) => {
    setSelectedPermission(permissionKey);
  };

  const handleCreateRole = (roleData) => {
    try {
      rbacService.createCustomRole(
        roleData.roleKey,
        roleData.name,
        roleData.description,
        [],
        roleData.level
      );
      
      // Update local state
      setRoles(prevRoles => {
        const newRoles = { ...prevRoles };
        newRoles[roleData.roleKey] = {
          name: roleData.name,
          description: roleData.description,
          permissions: [],
          level: roleData.level
        };
        return newRoles;
      });
      
      setRefreshKey(prev => prev + 1);
      
      // Audit log
      auditService.logCreate('roles', roleData.roleKey, roleData.name, {
        level: roleData.level,
        description: roleData.description
      });
      
      console.log('✅ Custom role created successfully');
    } catch (error) {
      console.error('❌ Error creating role:', error);
      setError('Failed to create role');
    }
  };

  const handleOpenUpdateModal = (roleKey) => {
    if (!roles[roleKey]) {
      console.error('Role not found:', roleKey);
      alert(t('roleNotFound'));
      return;
    }
    setRoleToUpdate(roleKey);
    setUpdatePermissions(roles[roleKey]?.permissions || []);
    setShowUpdateModal(true);
  };

  const handleUpdateRole = () => {
    try {
      rbacService.updateRolePermissions(roleToUpdate, updatePermissions);
      
      // Update local state
      setRoles(prevRoles => {
        const newRoles = { ...prevRoles };
        newRoles[roleToUpdate] = {
          ...newRoles[roleToUpdate],
          permissions: [...updatePermissions]
        };
        return newRoles;
      });
      
      setShowUpdateModal(false);
      setRoleToUpdate(null);
      setUpdatePermissions([]);
      
      // Audit log
      auditService.logUpdate('roles', roleToUpdate, roles[roleToUpdate].name, {
        permissionsCount: updatePermissions.length,
        action: 'Updated role permissions'
      });
      
      console.log('✅ Role permissions updated successfully');
    } catch (error) {
      console.error('❌ Error updating role:', error);
      setError(t('failedToUpdateRole'));
    }
  };

  const togglePermissionInUpdate = (permissionKey) => {
    setUpdatePermissions(prev => {
      if (prev.includes(permissionKey)) {
        return prev.filter(p => p !== permissionKey);
      } else {
        return [...prev, permissionKey];
      }
    });
  };

  const togglePermission = (roleKey, permissionKey) => {
    try {
      console.log(`🔍 Toggling permission ${permissionKey} for role ${roleKey}`);
      
      // Update local state first for immediate visual feedback
      setRoles(prevRoles => {
        const newRoles = { ...prevRoles };
        if (!newRoles[roleKey]) {
          console.error('Role not found in state:', roleKey);
          return prevRoles;
        }
        
        newRoles[roleKey] = { ...newRoles[roleKey] };
        const currentPermissions = newRoles[roleKey].permissions || [];
        const isCurrentlyGranted = currentPermissions.includes(permissionKey);
        
        console.log(`🔍 Current permissions for ${roleKey}:`, currentPermissions);
        console.log(`🔍 Is ${permissionKey} granted:`, isCurrentlyGranted);
        
        if (isCurrentlyGranted) {
          // Remove permission
          newRoles[roleKey].permissions = currentPermissions.filter(p => p !== permissionKey);
          console.log(`🔍 Removed ${permissionKey}, new permissions:`, newRoles[roleKey].permissions);
        } else {
          // Add permission
          newRoles[roleKey].permissions = [...currentPermissions, permissionKey];
          console.log(`🔍 Added ${permissionKey}, new permissions:`, newRoles[roleKey].permissions);
        }
        
        return newRoles;
      });
      
      // Update RBAC service cache
      if (rbacService.removePermissionFromRole && rbacService.addPermissionToRole) {
        const role = rbacService.getRole(roleKey);
        const rolePermissions = role?.permissions || [];
        const isCurrentlyGranted = rolePermissions.includes(permissionKey);
        
        if (isCurrentlyGranted) {
          rbacService.removePermissionFromRole(roleKey, permissionKey);
        } else {
          rbacService.addPermissionToRole(roleKey, permissionKey);
        }
      }
      
      console.log(`✅ Permission ${permissionKey} toggled for role ${roleKey}`);
    } catch (error) {
      console.error('Error toggling permission:', error);
      alert('Error updating permission');
    }
  };

  // Assign unique colors to each role
  const getRoleColor = (roleKey) => {
    const roleColors = {
      'management': '#e53e3e',           // Red
      'ic_personnel': '#dd6b20',         // Orange
      'administration': '#d69e2e',       // Yellow
      'execution': '#38a169',            // Green
      'prescriptors': '#3182ce',         // Blue
      'medical_director': '#805ad5',     // Purple
      'head_nurse': '#d53f8c',           // Pink
      'department_head': '#00b5d8',      // Cyan
      'senior_physician': '#319795',     // Teal
      'physician': '#4299e1',            // Light Blue
      'nurse': '#48bb78',                // Light Green
      'technician': '#ed8936',           // Light Orange
      'support_staff': '#718096',        // Gray
      'pharmacist': '#9f7aea',           // Light Purple
      'lab_technician': '#f56565',       // Light Red
    };
    
    // Return assigned color or generate a consistent color based on role key
    if (roleColors[roleKey]) {
      return roleColors[roleKey];
    }
    
    // Generate a color based on the role key hash for custom roles
    let hash = 0;
    for (let i = 0; i < roleKey.length; i++) {
      hash = roleKey.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 65%, 50%)`;
  };

  const getRoleLevelColor = (level) => {
    if (level >= 90) return '#dc3545'; // Red for high-level roles
    if (level >= 70) return '#fd7e14'; // Orange for management roles
    if (level >= 50) return '#ffc107'; // Yellow for professional roles
    if (level >= 30) return '#20c997'; // Teal for technical roles
    return '#6c757d'; // Gray for basic roles
  };

  const getPermissionCategory = (permission) => {
    const [category] = permission.split(':');
    return category;
  };

  const groupedPermissions = Object.keys(permissions).reduce((groups, permission) => {
    const category = getPermissionCategory(permission);
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(permission);
    return groups;
  }, {});

  return (
    <div className="clinical-mapper">
      {/* Navigation Tabs */}
      <div className="content-card">
        <div className="card-header">
          <div className="card-title">{t('title')}</div>
        </div>
        <div className="card-content">
          <div className="rbac-tabs">
            <button 
              className={`btn btn-secondary ${activeTab === 'roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('roles')}
            >
              {t('tabRoles')}
            </button>
            <button 
              className={`btn btn-secondary ${activeTab === 'users-roles' ? 'active' : ''}`}
              onClick={() => setActiveTab('users-roles')}
            >
              {t('tabUsersRoles')}
            </button>
            <button 
              className={`btn btn-secondary ${activeTab === 'matrix' ? 'active' : ''}`}
              onClick={() => setActiveTab('matrix')}
            >
              {t('tabMatrix')}
            </button>
            <button 
              className={`btn btn-secondary ${activeTab === 'manage' ? 'active' : ''}`}
              onClick={() => setActiveTab('manage')}
            >
              {t('tabManage')}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('rolesCount', { count: Object.keys(roles).length })}</div>
            <button onClick={() => setShowRoleModal(true)} className="btn btn-primary">
              {t('addRole')}
            </button>
          </div>
          <div className="card-content">
            {loadingRoles ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('loadingRoles')}</p>
              </div>
            ) : (
              <div className="entity-list">
                {Object.entries(roles).map(([roleKey, role]) => (
                  <div key={roleKey}>
                    <div 
                      className={`entity-item ${selectedRole === roleKey ? 'selected' : ''}`}
                      onClick={() => handleRoleSelect(roleKey)}
                    >
                      <div className="entity-info">
                        <div className="entity-name">{role.name}</div>
                        <div className="entity-details">
                          <span>{role.description}</span>
                          <span className="status" style={{ backgroundColor: getRoleColor(roleKey) }}>
                            {t('level')} {role.level}
                          </span>
                          <span>{t('permissionsCount', { count: role.permissions.length })}</span>
                        </div>
                      </div>
                      <div className="entity-actions">
                        <button 
                          className="btn btn-edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRoleSelect(roleKey);
                          }}
                        >
                          {selectedRole === roleKey ? t('hide') : t('view')}
                        </button>
                      </div>
                    </div>
                    
                    {/* Role Details Inline */}
                    {selectedRole === roleKey && roles[selectedRole] && (
                      <div className="role-details-inline" style={{ 
                        margin: '10px 0', 
                        padding: '20px', 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '8px',
                        border: '1px solid #e9ecef'
                      }}>
                        <div className="form-grid">
                          <div className="form-field">
                            <label>{t('roleName')}</label>
                            <input
                              type="text"
                              value={roles[selectedRole]?.name || ''}
                              readOnly
                              style={{ backgroundColor: '#fff' }}
                            />
                          </div>

                          <div className="form-field">
                            <label>{t('roleLevel')}</label>
                            <input
                              type="number"
                              value={roles[selectedRole]?.level || 0}
                              readOnly
                              style={{ backgroundColor: '#fff' }}
                            />
                          </div>

                          <div className="form-field full-width">
                            <label>{t('description')}</label>
                            <textarea
                              value={roles[selectedRole]?.description || ''}
                              readOnly
                              rows={2}
                              style={{ backgroundColor: '#fff' }}
                            />
                          </div>

                          <div className="form-field full-width">
                            <label>{t('assignedPermissions', { count: (roles[selectedRole]?.permissions || []).length })}</label>
                            <div className="permissions-list" style={{ 
                              display: 'flex', 
                              flexWrap: 'wrap', 
                              gap: '8px',
                              marginTop: '8px'
                            }}>
                              {(roles[selectedRole]?.permissions || []).map(permission => (
                                <div key={permission} style={{
                                  backgroundColor: '#e3f2fd',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  border: '1px solid #bbdefb'
                                }}>
                                  <span style={{ fontWeight: 'bold' }}>{permission}</span>
                                  {permissions[permission] && typeof permissions[permission] === 'string' && (
                                    <span style={{ marginLeft: '4px', color: '#666' }}>
                                      - {permissions[permission]}
                                    </span>
                                  )}
                                </div>
                              ))}
                              {(roles[selectedRole]?.permissions || []).length === 0 && (
                                <p style={{ color: '#666', fontStyle: 'italic' }}>{t('noPermissionsAssigned')}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Users & Roles Tab */}
      {activeTab === 'users-roles' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('usersAndRolesTitle', { count: users.length })}</div>
            <button 
              onClick={() => setRefreshKey(prev => prev + 1)} 
              className="btn btn-primary"
              disabled={loadingUsers}
            >
              {loadingUsers ? t('loading') : t('refresh')}
            </button>
          </div>
          <div className="card-content">
            {loadingUsers ? (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>{t('loadingUsersAndRoles')}</p>
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <p>{t('noUsersCreateFirst')}</p>
              </div>
            ) : (
              <div className="entity-list">
                {users.map((user, index) => (
                  <div key={user.id ?? user.uid ?? `user-${index}`} style={{ 
                    padding: '24px',
                    marginBottom: '20px',
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                  }}>
                    {/* Header with Name and Level */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '20px',
                      paddingBottom: '16px',
                      borderBottom: '2px solid #f1f5f9'
                    }}>
                      <h3 style={{ 
                        margin: 0, 
                        fontSize: '20px', 
                        fontWeight: '600', 
                        color: '#1a202c' 
                      }}>
                        {user.fullName || user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || t('unknownUser')}
                      </h3>
                      <span style={{ 
                        color: '#64748b', 
                        fontSize: '14px',
                        backgroundColor: '#f1f5f9',
                        padding: '6px 16px',
                        borderRadius: '20px',
                        fontWeight: '500'
                      }}>
                        {t('level')} {roles[user.roleId || user.role_id]?.level || 'N/A'}
                      </span>
                    </div>

                    {/* Main Content Grid */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                      gap: '24px',
                      alignItems: 'start'
                    }}>
                      {/* Contact Information Column */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px' 
                      }}>
                        <h4 style={{ 
                          margin: '0 0 12px 0', 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#475569',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('contactInformation')}
                        </h4>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            padding: '8px 12px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px'
                          }}>
                            <span style={{ color: '#334155', fontWeight: '500' }}>{user.email}</span>
                          </div>
                          {user.phone && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              padding: '8px 12px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '8px'
                            }}>
                              <span style={{ fontSize: '18px' }}>📞</span>
                              <span style={{ color: '#334155', fontWeight: '500' }}>{user.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Employee Information Column */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px' 
                      }}>
                        <h4 style={{ 
                          margin: '0 0 12px 0', 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#475569',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('employeeInformation')}
                        </h4>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          {user.badgeId && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              padding: '8px 12px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '8px'
                            }}>
                              <span style={{ color: '#334155', fontWeight: '500' }}>{t('badge')}: {user.badgeId}</span>
                            </div>
                          )}
                          {user.employeeId && (
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px',
                              padding: '8px 12px',
                              backgroundColor: '#f8fafc',
                              borderRadius: '8px'
                            }}>
                              <span style={{ fontSize: '18px' }}>👤</span>
                              <span style={{ color: '#334155', fontWeight: '500' }}>{t('employeeIdLabel')}: {user.employeeId}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Role & Status Column */}
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '12px' 
                      }}>
                        <h4 style={{ 
                          margin: '0 0 12px 0', 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          color: '#475569',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {t('roleAndStatus')}
                        </h4>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            padding: '8px 12px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px'
                          }}>
                            <span style={{ fontSize: '18px' }}>🏢</span>
                            <span style={{ 
                              backgroundColor: '#e2e8f0',
                              color: '#2d3748',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}>
                              {user.departmentName || user.department_name || user.department || t('noDepartment')}
                            </span>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            padding: '8px 12px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px'
                          }}>
                            <span style={{ fontSize: '18px' }}>
                              {user.status === 'Active' ? 'Active' : 'Inactive'}
                            </span>
                            <span style={{ 
                              backgroundColor: user.status === 'Active' ? '#48bb78' : '#e53e3e',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}>
                              {user.status}
                            </span>
                          </div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '10px',
                            padding: '8px 12px',
                            backgroundColor: '#f8fafc',
                            borderRadius: '8px'
                          }}>
                            <span style={{ fontSize: '18px' }}>👤</span>
                            <span style={{ 
                              backgroundColor: getRoleColor(user.roleId || user.role_id),
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '20px',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}>
                              {user.roleName || user.role_name || roles[user.roleId || user.role_id]?.name || 'Unknown Role'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}


      {/* Matrix Tab */}
      {activeTab === 'matrix' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('matrixTitle')}</div>
          </div>
          <div className="card-content">
            <div className="matrix-container">
              <table className="matrix-table">
                  <thead>
                    <tr>
                      <th>Permission</th>
                      {Object.entries(roles).map(([roleKey, role]) => (
                        <th key={roleKey} className="role-header">
                          <div className="role-header-info">
                            <span className="role-name">{role.name}</span>
                            <span 
                              className="role-level-badge"
                              style={{ backgroundColor: getRoleColor(roleKey) }}
                            >
                              L{role.level}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(permissions).map(([permission, description]) => (
                      <tr key={permission}>
                        <td className="permission-cell">
                          <div className="permission-info">
                            <span className="permission-key">{permission}</span>
                            <span className="permission-description">
                              {typeof description === 'string' 
                                ? description 
                                : description?.description || permission
                              }
                            </span>
                          </div>
                        </td>
                        {Object.entries(roles).map(([roleKey, role]) => {
                          const rolePermissions = role?.permissions || [];
                          const isChecked = rolePermissions.includes(permission);
                          const roleColor = getRoleColor(roleKey);
                          
                          return (
                            <td key={roleKey} className="role-permission-cell">
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => togglePermission(roleKey, permission)}
                                />
                                <span 
                                  className="toggle-slider"
                                  style={isChecked ? { backgroundColor: roleColor } : {}}
                                ></span>
                              </label>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          </div>
        </div>
      )}

      {/* Manage Tab */}
      {activeTab === 'manage' && (
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">{t('manageRolesTitle')}</div>
            <div>
              <button 
                className="btn btn-primary"
                onClick={() => setShowRoleModal(true)}
              >
                {t('createRole')}
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  const issues = rbacService.validatePermissions();
                  if (issues.length === 0) {
                    alert(t('allPermissionsValid'));
                  } else {
                    alert(t('permissionIssues') + '\n' + issues.join('\n'));
                  }
                }}
              >
                {t('validatePermissions')}
              </button>
            </div>
          </div>
          <div className="card-content">
            <div className="entity-list">
              {Object.keys(roles).map(roleKey => (
                <div key={roleKey} className="entity-item">
                  <div className="entity-info">
                    <div className="entity-name">{roles[roleKey].name}</div>
                    <div className="entity-details">
                      <span>{roles[roleKey].description}</span>
                      <span className="status" style={{ backgroundColor: getRoleColor(roleKey) }}>
                        {t('level')} {roles[roleKey].level}
                      </span>
                    </div>
                  </div>
                  <div className="entity-actions">
                    <button 
                      className="btn btn-edit btn-icon"
                      onClick={() => handleOpenUpdateModal(roleKey)}
                      title={t('updateRole')}
                    >
                      <Edit size={16} />
                    </button>
                    
                    {!['management', 'ic_personnel', 'administration', 'execution', 'prescriptors'].includes(roleKey) && (
                      <button 
                        className="btn btn-delete btn-icon"
                        onClick={() => {
                          if (window.confirm(t('deleteRoleConfirm', { name: roles[roleKey].name }))) {
                            rbacService.deleteRole(roleKey);
                            alert(t('roleDeleted'));
                            window.location.reload();
                          }
                        }}
                        title={t('deleteRole')}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Role Creation Modal */}
      <RoleCreationModal
        isOpen={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        onCreateRole={handleCreateRole}
      />

      {/* Update Role Permissions Modal */}
      {showUpdateModal && roleToUpdate && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h3>{t('updatePermissionsTitle', { name: roles[roleToUpdate]?.name || roleToUpdate })}</h3>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowUpdateModal(false);
                  setRoleToUpdate(null);
                  setUpdatePermissions([]);
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-content">
              <div className="form-field full-width">
                <label>
                  {t('selectedPermissions', { count: updatePermissions.length })}
                </label>
                <div className="permissions-list" style={{ marginBottom: '20px' }}>
                  {updatePermissions.map(permission => (
                    <div key={permission} className="permission-badge">
                      <span className="permission-key">{permission}</span>
                      <button
                        onClick={() => togglePermissionInUpdate(permission)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#e53e3e',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '0 4px'
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  {updatePermissions.length === 0 && (
                    <p className="empty-state">{t('noPermissionsSelected')}</p>
                  )}
                </div>
              </div>

              <div className="form-field full-width">
                <label>{t('availablePermissions')}</label>
                {Object.entries(groupedPermissions).map(([category, categoryPermissions]) => (
                  <div key={category} className="permission-category" style={{ marginBottom: '20px' }}>
                    <h4 className="category-title">{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {categoryPermissions.map(permission => {
                        const isSelected = updatePermissions.includes(permission);
                        return (
                          <div
                            key={permission}
                            onClick={() => togglePermissionInUpdate(permission)}
                            style={{
                              padding: '12px',
                              border: `2px solid ${isSelected ? getRoleColor(roleToUpdate) : '#e2e8f0'}`,
                              borderRadius: '6px',
                              cursor: 'pointer',
                              backgroundColor: isSelected ? `${getRoleColor(roleToUpdate)}10` : '#ffffff',
                              transition: 'all 0.2s'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => togglePermissionInUpdate(permission)}
                                style={{ cursor: 'pointer' }}
                              />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 600, color: '#2d3748', marginBottom: '4px' }}>
                                  {permission}
                                </div>
                                <div style={{ fontSize: '13px', color: '#718096' }}>
                                  {typeof permissions[permission] === 'string' 
                                    ? permissions[permission] 
                                    : permissions[permission]?.description || permission
                                  }
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-actions">
                <button 
                  className="btn btn-primary"
                  onClick={handleUpdateRole}
                >
                  {t('saveChanges')}
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setShowUpdateModal(false);
                    setRoleToUpdate(null);
                    setUpdatePermissions([]);
                  }}
                >
                  {t('cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RBACManagement;
